// pages/module-pages/faq/FaqsPage.tsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff, GripVertical } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useFaqs from "../../../hooks/features/faqs/useFaqs";
import { Faq } from "../../../types/features/Faq";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import FaqsList from "../../../components/shared/faqs/FaqsList";
import { useProduct } from "../../../hooks/core/useProduct";

export default function FaqsPage() {
  const navigate = useNavigate();
  const { selectedProduct } = useProduct();
  const { faqs, deleteFaq, deleteManyFaqs, toggleVisibility, reorderFaqs, loading } = useFaqs();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const [faqToDelete, setFaqToDelete] = useState<Faq | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // ✅ Filtrer les FAQ par produit
  const faqsByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return faqs.filter((f) => f.product_name === selectedProduct);
    }
    return faqs;
  }, [faqs, selectedProduct]);

  // ✅ Stats basées sur les FAQ filtrées par produit
  const stats = {
    total: faqsByProduct.length,
    visible: faqsByProduct.filter((f) => f.is_visible).length,
    hidden: faqsByProduct.filter((f) => !f.is_visible).length,
  };

  // ✅ Filtrer les FAQ (recherche + statut) à partir des FAQ déjà filtrées par produit
  const filteredFaqs = useMemo(() => {
    let result = faqsByProduct;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (f) =>
          f.question.toLowerCase().includes(searchLower) ||
          f.answer.toLowerCase().includes(searchLower) ||
          f.question_en.toLowerCase().includes(searchLower) ||
          f.answer_en.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus) {
      const isVisible = selectedStatus === "visible";
      result = result.filter((f) => f.is_visible === isVisible);
    }

    return result;
  }, [faqsByProduct, searchTerm, selectedStatus]);

  const handleToggleVisibility = async (faq: Faq) => {
    const success = await toggleVisibility(faq.id, faq.is_visible);
    if (success) {
      alertSuccess(faq.is_visible ? "FAQ masquée" : "FAQ affichée");
    } else {
      alertError("Erreur lors du changement de statut");
    }
  };

  const handleDelete = async () => {
    if (!faqToDelete) return;
    setIsDeleting(true);
    const success = await deleteFaq(faqToDelete.id);
    setIsDeleting(false);
    if (success) {
      setFaqToDelete(null);
      alertSuccess("FAQ supprimée avec succès");
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    const success = await deleteManyFaqs(itemsToDelete);
    if (success) {
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} FAQ supprimées`);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const handleReorder = async () => {
    const currentIds = filteredFaqs.map((f) => f.id);
    setIsReordering(true);
    const success = await reorderFaqs(currentIds);
    setIsReordering(false);
    if (success) {
      alertSuccess("Ordre mis à jour");
    } else {
      alertError("Erreur lors du réordonnancement");
    }
  };

  const clearFilters = () => {
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedStatus || searchTerm;

  const getActionsMenu = (faq: Faq): Menu[] => {
    return [
      {
        label: faq.is_visible ? "Masquer" : "Afficher",
        icon: faq.is_visible ? EyeOff : Eye,
        onClick: () => handleToggleVisibility(faq),
      },
      {
        label: "Modifier",
        icon: Plus,
        onClick: () => navigate(`/admin/faq/update?id=${faq.id}`),
      },
      {
        label: "Supprimer",
        icon: Plus,
        onClick: () => setFaqToDelete(faq),
      },
    ];
  };

  return (
    <PageLayout title="Gestion des FAQ" className="min-h-screen bg-gray-50">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">FAQ</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les questions fréquemment posées</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReorder}
                disabled={isReordering || filteredFaqs.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 disabled:opacity-50"
              >
                <GripVertical size={18} />
                {isReordering ? "Mise à jour..." : "Appliquer l'ordre"}
              </button>
              <button
                onClick={() => navigate("/admin/faq/new")}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90"
              >
                <Plus size={18} />
                Nouvelle FAQ
              </button>
            </div>
          </div>

          {/* Indicateur de filtre produit */}
          {selectedProduct && selectedProduct !== 'all' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700">
              Filtré par produit : <span className="font-semibold">{selectedProduct}</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Visibles</p>
              <p className="text-2xl font-bold text-green-700">{stats.visible}</p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Masquées</p>
              <p className="text-2xl font-bold text-gray-700">{stats.hidden}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une FAQ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Tous</option>
                      <option value="visible">Visible</option>
                      <option value="hidden">Masqué</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <FaqsList
            faqs={filteredFaqs}
            onAction={(faq) => setSelectedFaq(faq)}
            onSelectFaqs={(selected) => setItemsToDelete(selected.map((f) => f.id))}
            selectable={true}
            selectActions={[
              {
                label: "Supprimer",
                onClick: (selected) => setItemsToDelete(selected.map((f) => f.id)),
                className: "bg-red-600 text-white",
              },
            ]}
          />
        )}
      </div>

      {/* Menu modal */}
      {selectedFaq && (
        <MenuModal
          title={selectedFaq.question}
          isOpen={!!selectedFaq}
          onClose={() => setSelectedFaq(null)}
          icon={null}
          menu={getActionsMenu(selectedFaq)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!faqToDelete}
        onClose={() => setFaqToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la FAQ"
        message={`Supprimer "${faqToDelete?.question}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} FAQ ?`}
      />
    </PageLayout>
  );
}